import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import {
  adminListModules, adminGetModule, adminCreateModule, adminUpdateModule, adminDeleteModule,
  adminUploadModuleImage, adminUploadItemAudio, adminClearItemAudio,
  adminBulkUploadItemAudio, adminUploadPassageAudio,
  adminUploadAyahAudio, adminClearAyahAudio, adminUploadAyahNewWordsAudio, adminClearAyahNewWordsAudio,
  adminUpdateAyah, adminAddAyah, adminDeleteAyah,
  adminUpdateInstructionsText, adminUploadInstructionsAudio, adminClearInstructionsAudio,
  adminUploadItemPicture, adminClearItemPicture,
  adminTtsPreview, adminTtsWarmBatch,
  adminGetConfig, adminUpdateConfig,
  adminListLocales, adminGenerateModuleLocale,
  adminGetModuleTranslations, adminSetModuleTranslations,
} from "@/lib/apiClient";
import { Layers, Clock, Plus, Save, Trash2, Image as ImageIcon, Upload, X, ChevronLeft, Edit2, Volume2, FileAudio, Zap, Loader2, Settings, ChevronDown, ChevronUp, AlertTriangle, Check, Wand2 } from "lucide-react";
import GroupsEditor from "./GroupsEditor";
import SectionTimingsEditor from "./SectionTimingsEditor";
import AdminAudioRecorderButton from "./AdminAudioRecorderButton";
import { useAuth } from "@/contexts/AuthContext";
import { listTimezones, timezoneLabel, zonedTimeToUtcIso, utcIsoToZonedParts, browserTimezone } from "@/lib/timezones";

const CONTENT_TYPES = [
  { code: "reading", label: "Reading (timed)" },
  { code: "mcq", label: "Multiple-choice (sound vocab)" },
  { code: "recording", label: "Recording (final recitation)" },
];

export default function AdminModules() {
  const { user } = useAuth();
  // A Translator reaches this page only to edit per-locale module metadata
  // (name/intro) for their own assigned_locales — everything else here
  // (create/delete, content editing, groups, hero image, App Configuration,
  // machine-translate Generate) stays Admin/Support-only, enforced on the
  // backend by routes/admin.py's require_admin_or_support on those routes.
  const isTranslator = user?.role === "translator";
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);   // { code: "..." } or { isNew: true }
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("modules"); // "modules" | "config"

  const refresh = async () => {
    setLoading(true);
    try { setRows(await adminListModules()); } catch (e) { toast.error("Couldn't load modules"); }
    finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, []);

  if (editing) {
    return <ModuleEditor target={editing} onClose={() => { setEditing(null); refresh(); }} />;
  }

  return (
    <div data-testid="admin-modules-page">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div>
          <div className="text-xs uppercase tracking-[0.22em] text-[#4B5563]">Admin · Modules</div>
          <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-[#1F2937] mt-1">Modules CMS</h1>
          <p className="text-sm text-[#4B5563] mt-1">Create, edit, reorder and disable modules. Changes go live immediately.</p>
        </div>
        {view === "modules" && !isTranslator && (
          <button data-testid="create-module-btn" onClick={() => setEditing({ isNew: true })}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#064E3B] hover:bg-[#047857] text-white text-sm font-medium transition">
            <Plus className="w-4 h-4" /> New module
          </button>
        )}
      </div>

      {!isTranslator && (
        <div className="inline-flex items-center rounded-full border border-[#E7E0D2] bg-white overflow-hidden mb-6" data-testid="admin-modules-view-toggle">
          <button data-testid="admin-modules-view-modules" onClick={() => setView("modules")}
            className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium transition ${view === "modules" ? "bg-[#064E3B] text-white" : "text-[#4B5563] hover:text-[#1F2937]"}`}>
            <Layers className="w-4 h-4" /> Modules
          </button>
          <button data-testid="admin-modules-view-config" onClick={() => setView("config")}
            className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium transition ${view === "config" ? "bg-[#064E3B] text-white" : "text-[#4B5563] hover:text-[#1F2937]"}`}>
            <Settings className="w-4 h-4" /> App Configuration
          </button>
        </div>
      )}

      {!isTranslator && view === "config" ? <AppConfigPanel /> : (
      <>
      {!isTranslator && <TtsWarmPanel />}

      <div className="rounded-2xl bg-white border border-[#E7E0D2] overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#FDFBF7] text-[#4B5563] text-xs uppercase tracking-[0.16em]">
            <tr>
              <th className="px-4 py-3 text-left">#</th>
              <th className="px-4 py-3 text-left">Code · Name</th>
              <th className="px-4 py-3 text-left">Type</th>
              <th className="px-4 py-3 text-right">Target</th>
              <th className="px-4 py-3 text-right">Items</th>
              <th className="px-4 py-3 text-center">Status</th>
              <th className="px-4 py-3 text-right">—</th>
            </tr>
          </thead>
          <tbody>
            {loading && (<tr><td colSpan={7} className="px-4 py-8 text-center text-[#4B5563]">Loading…</td></tr>)}
            {!loading && rows.map((m) => (
              <tr key={m.code} data-testid={`module-row-${m.code}`} className="border-t border-[#E7E0D2] hover:bg-[#FDFBF7]">
                <td className="px-4 py-3 font-mono text-xs text-[#4B5563]">{m.n}</td>
                <td className="px-4 py-3">
                  <div className="font-medium text-[#1F2937]">{m.name}</div>
                  <div className="text-xs text-[#4B5563] font-mono">{m.code}</div>
                </td>
                <td className="px-4 py-3 text-[#4B5563] capitalize">{m.content_type}</td>
                <td className="px-4 py-3 text-right tabular-nums">{m.target_seconds > 0 ? `${m.target_seconds}s` : "—"}</td>
                <td className="px-4 py-3 text-right tabular-nums">
                  {m.content_type === "recording"
                    ? (m.passages?.length || 0) + " passages"
                    : (m.items?.length || 0)}
                </td>
                <td className="px-4 py-3 text-center">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${
                    m.enabled !== false ? "bg-[#ECFDF5] text-[#065F46]" : "bg-[#FEF2F2] text-[#7F1D1D]"
                  }`}>{m.enabled !== false ? "Active" : "Disabled"}</span>
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="inline-flex items-center gap-1.5 justify-end">
                    {!isTranslator && <GenerateButton m={m} />}
                    <button data-testid={`edit-module-${m.code}`} onClick={() => setEditing({ code: m.code })}
                      className="inline-flex items-center gap-1.5 text-xs px-3 py-1 rounded-full border border-[#E7E0D2] hover:border-[#064E3B] text-[#1F2937]">
                      <Edit2 className="w-3.5 h-3.5" /> Edit
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      </>
      )}
    </div>
  );
}

/* -------------------- Generate button (per-module locale generation) --------------------
   Like the Users-page row menu, the locale list is portalled to `document.body`
   and positioned from the trigger's `getBoundingClientRect()`. The modules table
   wrapper needs `overflow-hidden` for its rounded corners, which would otherwise
   clip this menu off for the bottom rows. Position is recomputed once the locale
   list arrives, because the menu's height isn't known until then. */
const LOCALE_MENU_W = 220;
const LOCALE_ROW_H = 45;

function GenerateButton({ m }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);      // { top, left }
  const [locales, setLocales] = useState(null);
  const [mtConfigured, setMtConfigured] = useState(true);
  const [busy, setBusy] = useState(null);
  const [generated, setGenerated] = useState(new Set());
  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  // Anchor the menu under (or above, near the viewport bottom) the trigger.
  const computePos = useCallback(() => {
    if (!triggerRef.current) return;
    const r = triggerRef.current.getBoundingClientRect();
    const rows = locales === null ? 1 : Math.max(1, locales.length);
    const menuH = rows * LOCALE_ROW_H + 8;
    const flip = r.bottom + menuH > window.innerHeight - 12;
    const top = flip ? Math.max(8, r.top - menuH - 6) : r.bottom + 6;
    const left = Math.min(window.innerWidth - LOCALE_MENU_W - 8, Math.max(8, r.right - LOCALE_MENU_W));
    setPos({ top, left });
  }, [locales]);

  const handleOpen = async () => {
    if (!open) computePos();
    setOpen((v) => !v);
    if (!locales) {
      try {
        const data = await adminListLocales();
        setMtConfigured(data.locales.some((l) => l.mt_configured));
        setLocales(data.locales.filter((l) => l.status === "published" && l.code !== "en"));
      } catch { setLocales([]); }
    }
  };

  // Re-anchor once the list loads and the real height is known.
  useEffect(() => { if (open) computePos(); }, [open, locales, computePos]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (
        triggerRef.current && !triggerRef.current.contains(e.target) &&
        menuRef.current && !menuRef.current.contains(e.target)
      ) setOpen(false);
    };
    const onEsc = (e) => { if (e.key === "Escape") setOpen(false); };
    const onScroll = () => setOpen(false);
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onEsc);
    window.addEventListener("scroll", onScroll, true);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onEsc);
      window.removeEventListener("scroll", onScroll, true);
    };
  }, [open]);

  const generate = async (l) => {
    if (busy) return;
    setBusy(l.code);
    try {
      const r = await adminGenerateModuleLocale(m.code, l.code);
      toast.success(r.translated > 0
        ? `Translated ${r.translated} field(s) to ${l.label}`
        : `${l.label} already has content — nothing to generate`);
      setGenerated((prev) => new Set([...prev, l.code]));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Translation failed");
    } finally { setBusy(null); }
  };

  const hasContent = (code) =>
    generated.has(code) || Boolean((m.instructions?.[code]?.text || "").trim());

  return (
    <>
      <button
        ref={triggerRef}
        data-testid={`generate-module-${m.code}`}
        onClick={handleOpen}
        disabled={locales !== null && !mtConfigured}
        title={!locales || mtConfigured ? "Generate translations for this module" : "Set GOOGLE_TRANSLATE_API_KEY to enable"}
        className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full border border-[#E7E0D2] hover:border-[#064E3B] text-[#1F2937] disabled:opacity-40 disabled:cursor-not-allowed">
        <Wand2 className="w-3 h-3" /> Generate
      </button>

      {open && pos && createPortal(
        <div
          ref={menuRef}
          data-testid={`generate-module-menu-${m.code}`}
          style={{ position: "fixed", top: pos.top, left: pos.left, width: LOCALE_MENU_W }}
          className="z-[60] bg-white border border-[#E7E0D2] rounded-2xl shadow-xl overflow-hidden max-h-[60vh] overflow-y-auto">
          {!locales ? (
            <div className="flex items-center gap-2 text-sm text-[#4B5563] px-4 py-3">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…
            </div>
          ) : locales.length === 0 ? (
            <div className="text-sm text-[#4B5563] px-4 py-3">No published languages yet.</div>
          ) : (
            <div>
              {locales.map((l) => {
                const has = hasContent(l.code);
                const loading = busy === l.code;
                return (
                  <button
                    key={l.code}
                    onClick={() => generate(l)}
                    disabled={!!busy}
                    className={`w-full flex items-center justify-between px-4 py-3 text-sm transition text-left disabled:cursor-not-allowed border-b border-[#F3EDE2] last:border-b-0 ${
                      has
                        ? "bg-[#FDF8F0] text-[#064E3B] font-medium"
                        : "text-[#1F2937] hover:bg-[#FDFBF7]"
                    }`}>
                    <span>{l.label}</span>
                    {loading
                      ? <Loader2 className="w-4 h-4 animate-spin text-[#064E3B] shrink-0" />
                      : has
                        ? <Check className="w-4 h-4 text-[#064E3B] shrink-0" />
                        : null}
                  </button>
                );
              })}
            </div>
          )}
        </div>,
        document.body
      )}
    </>
  );
}

/* -------------------- App configuration panel -------------------- */
/**
 * Global app settings — beta mode, peer-review knobs, sound-vocab passing
 * %. Moved here (under Modules) from the Analytics page; the per-content-
 * type `target_times` timer editor that used to live alongside these was
 * removed as a duplicate of the granular per-module/per-section timing
 * editor already on each module's Metadata tab.
 */

// Computed once at module load, not per render — this browser's IANA list
// never changes mid-session, and re-sorting a ~400-entry list on every
// keystroke elsewhere in the panel would be wasted work. UTC pinned first
// since it's the one entry an admin is likely to want without scrolling to
// find it alphabetically (it'd otherwise land past every "T"-and-earlier zone).
const TIMEZONES = (() => {
  const all = listTimezones();
  return ["UTC", ...all.filter((tz) => tz !== "UTC").sort()];
})();

function AppConfigPanel() {
  const [cfg, setCfg] = useState(null);
  // Kept as their own local strings (not derived live from cfg.zoom_scheduled_at)
  // so typing the date doesn't get wiped the moment it renders while the time
  // field is still empty — the pair only combines into one ISO instant, in
  // the selected zone, on save.
  const [zoomDate, setZoomDate] = useState("");
  const [zoomTime, setZoomTime] = useState("");
  const [zoomTimezone, setZoomTimezone] = useState("");

  // `isStale` lets a caller opt out of applying a response that's no longer
  // wanted — see the mount effect below. The post-save reload (`load()`,
  // called with no argument) always applies, since that call is a direct,
  // single result of the admin's own Save click, not a background fetch that
  // could be superseded by anything.
  const load = async (isStale = () => false) => {
    try {
      const c = await adminGetConfig();
      if (isStale()) return;
      setCfg(c);
      // Decode the stored instant using the zone it was SAVED in, not the
      // browser's — so an admin editing from a different machine/timezone
      // still sees exactly what they originally scheduled, not a
      // re-interpretation in wherever they happen to be right now.
      // `zoom_timezone` is always present (DEFAULT_CONFIG seeds "UTC" and
      // _get_config() backfills it for any older record missing the field);
      // `browserTimezone()` only exists as a safety net for the
      // unreachable-in-practice case of an explicitly empty value.
      const tz = c.zoom_timezone || browserTimezone();
      const { date, time } = utcIsoToZonedParts(c.zoom_scheduled_at, tz);
      setZoomTimezone(tz);
      setZoomDate(date);
      setZoomTime(time);
    } catch {
      if (!isStale()) toast.error("Couldn't load configuration");
    }
  };
  useEffect(() => {
    // React 18 StrictMode deliberately mounts effects twice in development,
    // firing two GET /admin/config requests back to back. Found this by
    // actually instrumenting it: both land within the same millisecond, but
    // resolve at different times — and whichever resolves *last* wins,
    // unconditionally overwriting `cfg` even if it arrives after the admin
    // has already started filling in fields from the first response. `ignore`
    // is this effect's own instance flag; React flips it via the cleanup
    // function the moment a second mount (or a real unmount) supersedes this
    // one, so a late, stale response is dropped instead of clobbering
    // whatever the admin has typed since. Never observed outside of
    // StrictMode's dev-only double-invoke, but it's a real gap in any
    // fetch-in-an-effect without this guard, so it's worth closing properly
    // rather than relying on production not happening to double-fire it.
    let ignore = false;
    load(() => ignore);
    return () => { ignore = true; };
  }, []);

  // Every field below updates via `setCfg((prev) => ({ ...prev, x }))`, not
  // `setCfg({ ...cfg, x })`. With this many fields sharing one `cfg` object,
  // the latter closes over whichever `cfg` was current when that particular
  // input's handler was created — if two fields change in the same React
  // batch (e.g. autofill, or edits close enough together not to force a
  // render in between), the second field's update silently overwrites the
  // first's with a stale copy. The functional form always starts from
  // React's own latest state, so this can't happen no matter how the edits
  // are batched.

  const save = async () => {
    try {
      const payload = {
        ...cfg,
        zoom_scheduled_at: zonedTimeToUtcIso(zoomDate, zoomTime, zoomTimezone),
        zoom_timezone: zoomTimezone || browserTimezone(),
      };
      await adminUpdateConfig(payload);
      toast.success("Config saved");
      load();
    } catch (e) {
      // Surfaces the backend's actual reason (e.g. "Zoom URL must be a valid
      // https:// link") instead of a generic failure message.
      toast.error(e?.response?.data?.detail || "Could not save");
    }
  };

  if (!cfg) return <div className="text-[#4B5563]">Loading…</div>;

  return (
    <div className="rounded-2xl bg-white border border-[#E7E0D2] p-5" data-testid="app-config-panel">
      <div className="text-xs uppercase tracking-widest text-[#4B5563]">App configuration</div>

      <div className="mt-4 grid sm:grid-cols-3 gap-4">
        <label className="flex items-center gap-3 p-3 rounded-xl border border-[#E7E0D2] bg-[#FDFBF7] cursor-pointer">
          <input type="checkbox" data-testid="cfg-beta-mode" checked={Boolean(cfg.beta_mode)}
            onChange={(e) => setCfg((prev) => ({ ...prev, beta_mode: e.target.checked }))} />
          <div>
            <div className="text-sm font-medium text-[#1F2937]">Beta mode</div>
            <div className="text-xs text-[#4B5563]">Unlock every module for testers regardless of previous results.</div>
          </div>
        </label>
        <label className="flex items-center gap-3 p-3 rounded-xl border border-[#E7E0D2] bg-[#FDFBF7] cursor-pointer">
          <input type="checkbox" data-testid="cfg-peer-review" checked={cfg.peer_review_enabled !== false}
            onChange={(e) => setCfg((prev) => ({ ...prev, peer_review_enabled: e.target.checked }))} />
          <div>
            <div className="text-sm font-medium text-[#1F2937]">Peer review enabled</div>
            <div className="text-xs text-[#4B5563]">Allow learners to submit recordings to community reviewers.</div>
          </div>
        </label>
        <label className="block p-3 rounded-xl border border-[#E7E0D2] bg-[#FDFBF7]">
          <div className="text-sm font-medium text-[#1F2937]">Min reviews per module</div>
          <input type="number" min={1} max={20} data-testid="cfg-peer-min"
            value={cfg.peer_review_min_reviews ?? 5}
            onChange={(e) => setCfg((prev) => ({ ...prev, peer_review_min_reviews: Number(e.target.value) }))}
            className="mt-1 w-full rounded-xl bg-white border border-[#E7E0D2] focus:border-[#064E3B] px-3 py-2 text-sm outline-none" />
          <div className="text-xs text-[#4B5563] mt-1">Earns the &ldquo;Peer Reviewed&rdquo; badge.</div>
        </label>
        <label className="flex items-center gap-3 p-3 rounded-xl border border-[#E7E0D2] bg-[#FDFBF7] cursor-pointer">
          <input type="checkbox" data-testid="cfg-whatsapp-support" checked={Boolean(cfg.whatsapp_support_enabled)}
            onChange={(e) => setCfg((prev) => ({ ...prev, whatsapp_support_enabled: e.target.checked }))} />
          <div>
            <div className="text-sm font-medium text-[#1F2937]">WhatsApp support button</div>
            <div className="text-xs text-[#4B5563]">Floating chat button (bottom-right, every page). Keep off until the Wati number/flow is finalized.</div>
          </div>
        </label>
      </div>

      {/* Live Zoom Class — backs the Dashboard's "Community & Live Learning"
          section (Dashboard.jsx's ZoomClassCard). Saved through the same
          app_config document and the same Save button as the settings above;
          disabling never clears zoom_url/date/time/title/description, so
          re-enabling brings the previous session straight back. */}
      <div className="mt-6">
        <div className="text-xs uppercase tracking-widest text-[#4B5563]">Live Zoom Class</div>
        <div className="mt-4 grid gap-4">
          <label className="flex items-center gap-3 p-3 rounded-xl border border-[#E7E0D2] bg-[#FDFBF7] cursor-pointer">
            <input type="checkbox" data-testid="cfg-zoom-enabled" checked={Boolean(cfg.zoom_enabled)}
              onChange={(e) => setCfg((prev) => ({ ...prev, zoom_enabled: e.target.checked }))} />
            <div>
              <div className="text-sm font-medium text-[#1F2937]">Enable live class</div>
              <div className="text-xs text-[#4B5563]">Shows the Join button on the dashboard. Turning this off keeps the details below for next time — they aren't deleted.</div>
            </div>
          </label>

          <label className="block p-3 rounded-xl border border-[#E7E0D2] bg-[#FDFBF7]">
            <div className="text-sm font-medium text-[#1F2937]">Zoom meeting URL</div>
            <input type="url" inputMode="url" placeholder="https://zoom.us/j/…" data-testid="cfg-zoom-url"
              value={cfg.zoom_url || ""}
              onChange={(e) => setCfg((prev) => ({ ...prev, zoom_url: e.target.value }))}
              className="mt-1 w-full rounded-xl bg-white border border-[#E7E0D2] focus:border-[#064E3B] px-3 py-2 text-sm outline-none" />
            <div className="text-xs text-[#4B5563] mt-1">Must be a secure https:// link — checked when you save.</div>
          </label>

          <div className="grid sm:grid-cols-3 gap-4">
            <label className="block p-3 rounded-xl border border-[#E7E0D2] bg-[#FDFBF7]">
              <div className="text-sm font-medium text-[#1F2937]">Date</div>
              <input type="date" data-testid="cfg-zoom-date"
                value={zoomDate}
                onChange={(e) => setZoomDate(e.target.value)}
                className="mt-1 w-full rounded-xl bg-white border border-[#E7E0D2] focus:border-[#064E3B] px-3 py-2 text-sm outline-none" />
            </label>
            <label className="block p-3 rounded-xl border border-[#E7E0D2] bg-[#FDFBF7]">
              <div className="text-sm font-medium text-[#1F2937]">Time</div>
              <input type="time" data-testid="cfg-zoom-time"
                value={zoomTime}
                onChange={(e) => setZoomTime(e.target.value)}
                className="mt-1 w-full rounded-xl bg-white border border-[#E7E0D2] focus:border-[#064E3B] px-3 py-2 text-sm outline-none" />
            </label>
            <label className="block p-3 rounded-xl border border-[#E7E0D2] bg-[#FDFBF7]">
              <div className="text-sm font-medium text-[#1F2937]">Time zone</div>
              <select data-testid="cfg-zoom-timezone"
                value={zoomTimezone}
                onChange={(e) => setZoomTimezone(e.target.value)}
                className="mt-1 w-full rounded-xl bg-white border border-[#E7E0D2] focus:border-[#064E3B] px-3 py-2 text-sm outline-none">
                {/* If the saved zone isn't in this browser's own list for some
                    reason, still show it as a selected option rather than
                    silently swap it to something else the admin didn't pick. */}
                {zoomTimezone && !TIMEZONES.includes(zoomTimezone) && (
                  <option value={zoomTimezone}>{timezoneLabel(zoomTimezone)}</option>
                )}
                {TIMEZONES.map((tz) => (
                  <option key={tz} value={tz}>{timezoneLabel(tz)}</option>
                ))}
              </select>
            </label>
          </div>
          <div className="text-xs text-[#4B5563] -mt-2">Date and time are in the zone selected above. Each student then sees it converted to their own local time and language.</div>

          <label className="block p-3 rounded-xl border border-[#E7E0D2] bg-[#FDFBF7]">
            <div className="text-sm font-medium text-[#1F2937]">Meeting title <span className="text-[#9CA3AF] font-normal">(optional)</span></div>
            <input type="text" placeholder="Quran Fluency Live Class" data-testid="cfg-zoom-title"
              value={cfg.zoom_title || ""}
              onChange={(e) => setCfg((prev) => ({ ...prev, zoom_title: e.target.value }))}
              className="mt-1 w-full rounded-xl bg-white border border-[#E7E0D2] focus:border-[#064E3B] px-3 py-2 text-sm outline-none" />
          </label>

          <label className="block p-3 rounded-xl border border-[#E7E0D2] bg-[#FDFBF7]">
            <div className="text-sm font-medium text-[#1F2937]">Description <span className="text-[#9CA3AF] font-normal">(optional)</span></div>
            <textarea rows={3} placeholder="Join us for our live Quran Fluency session." data-testid="cfg-zoom-description"
              value={cfg.zoom_description || ""}
              onChange={(e) => setCfg((prev) => ({ ...prev, zoom_description: e.target.value }))}
              className="mt-1 w-full px-3 py-2 rounded-xl bg-white border border-[#E7E0D2] focus:border-[#064E3B] outline-none text-sm resize-none" />
          </label>
        </div>
      </div>

      {/* Recorded Session — an independent fallback for the same card,
          shown to students only when there's no upcoming class (see
          ZoomClassCard's priority order in CommunityLiveLearning.jsx). Its
          own on/off + url/title/description, deliberately not reusing the
          Zoom fields above: an admin should be able to keep a past
          recording linked even while a new live class is scheduled, and
          disabling one must never clear the other. */}
      <div className="mt-6">
        <div className="text-xs uppercase tracking-widest text-[#4B5563]">Recorded Session</div>
        <div className="mt-1 text-xs text-[#4B5563]">
          Shown on the dashboard only when there is no upcoming live class enabled above — gives students something to watch instead of "Stay tuned".
        </div>
        <div className="mt-4 grid gap-4">
          <label className="flex items-center gap-3 p-3 rounded-xl border border-[#E7E0D2] bg-[#FDFBF7] cursor-pointer">
            <input type="checkbox" data-testid="cfg-recording-enabled" checked={Boolean(cfg.recording_enabled)}
              onChange={(e) => setCfg((prev) => ({ ...prev, recording_enabled: e.target.checked }))} />
            <div>
              <div className="text-sm font-medium text-[#1F2937]">Enable recorded session</div>
              <div className="text-xs text-[#4B5563]">Turning this off keeps the link and text below for next time — they aren't deleted.</div>
            </div>
          </label>

          <label className="block p-3 rounded-xl border border-[#E7E0D2] bg-[#FDFBF7]">
            <div className="text-sm font-medium text-[#1F2937]">Recording URL</div>
            <input type="url" inputMode="url" placeholder="https://youtube.com/watch?v=… or https://drive.google.com/…" data-testid="cfg-recording-url"
              value={cfg.recording_url || ""}
              onChange={(e) => setCfg((prev) => ({ ...prev, recording_url: e.target.value }))}
              className="mt-1 w-full rounded-xl bg-white border border-[#E7E0D2] focus:border-[#064E3B] px-3 py-2 text-sm outline-none" />
            <div className="text-xs text-[#4B5563] mt-1">Must be a secure https:// link — checked when you save.</div>
          </label>

          <label className="block p-3 rounded-xl border border-[#E7E0D2] bg-[#FDFBF7]">
            <div className="text-sm font-medium text-[#1F2937]">Title <span className="text-[#9CA3AF] font-normal">(optional)</span></div>
            <input type="text" placeholder="Last week's live class" data-testid="cfg-recording-title"
              value={cfg.recording_title || ""}
              onChange={(e) => setCfg((prev) => ({ ...prev, recording_title: e.target.value }))}
              className="mt-1 w-full rounded-xl bg-white border border-[#E7E0D2] focus:border-[#064E3B] px-3 py-2 text-sm outline-none" />
          </label>

          <label className="block p-3 rounded-xl border border-[#E7E0D2] bg-[#FDFBF7]">
            <div className="text-sm font-medium text-[#1F2937]">Description <span className="text-[#9CA3AF] font-normal">(optional)</span></div>
            <textarea rows={3} placeholder="Missed the live session? Watch the recording here." data-testid="cfg-recording-description"
              value={cfg.recording_description || ""}
              onChange={(e) => setCfg((prev) => ({ ...prev, recording_description: e.target.value }))}
              className="mt-1 w-full px-3 py-2 rounded-xl bg-white border border-[#E7E0D2] focus:border-[#064E3B] outline-none text-sm resize-none" />
          </label>
        </div>
      </div>

      <div className="mt-5 text-xs text-[#4B5563] bg-[#FDFBF7] border border-[#E7E0D2] rounded-xl px-3 py-2">
        Each module's own passing threshold (e.g. Module 8's "Pass percent") is set on that module's Metadata tab — that's the single source of truth now, not a global setting here.
      </div>

      <div className="mt-5 text-xs text-[#4B5563] bg-[#FDFBF7] border border-[#E7E0D2] rounded-xl px-3 py-2">
        Per-module timing (Maximum/Expert seconds) is configured on each module's own Metadata tab — that's the single source of truth for timers now.
      </div>

      <button data-testid="cfg-save" onClick={save}
        className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#064E3B] hover:bg-[#047857] text-white text-sm font-medium transition">
        <Save className="w-4 h-4" /> Save configuration
      </button>
    </div>
  );
}

/* -------------------- TTS warm-cache panel -------------------- */
/**
 * Pre-generates OpenAI TTS for every item, passage and intro across all
 * modules so the first student experience (and offline downloads) feel
 * instant. Already-cached entries are skipped server-side. Batches of 20
 * keep us under proxy timeouts.
 */
function TtsWarmPanel() {
  const [stats, setStats] = useState(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(null); // { done, total, generated, cached, failed }

  const load = async () => {
    try { setStats(await adminTtsPreview()); }
    catch { toast.error("Couldn't load TTS cache stats"); }
  };
  useEffect(() => { load(); }, []);

  const warmAll = async () => {
    if (busy || !stats) return;
    setBusy(true);
    const todo = stats.items.filter((it) => !it.cached).map((it) => it.text);
    if (todo.length === 0) { toast.success("Everything is already cached"); setBusy(false); return; }
    let generated = 0, cached = 0, failed = 0, done = 0;
    setProgress({ done, total: todo.length, generated, cached, failed });
    try {
      for (let i = 0; i < todo.length; i += 20) {
        const batch = todo.slice(i, i + 20);
        const r = await adminTtsWarmBatch(batch);
        generated += r.generated || 0;
        cached    += r.cached || 0;
        failed    += r.failed || 0;
        done      += batch.length;
        setProgress({ done, total: todo.length, generated, cached, failed });
      }
      toast.success(`Warmed cache · ${generated} generated, ${cached} already had, ${failed} failed`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Warm failed");
    } finally {
      setBusy(false);
      // Keep the final progress visible briefly so admins see the result.
      setTimeout(() => setProgress(null), 6000);
    }
  };

  if (!stats) return null;
  const pct = progress ? Math.round(100 * progress.done / Math.max(progress.total, 1)) : 0;
  const allCached = stats.uncached === 0;

  return (
    <div data-testid="tts-warm-panel" className="mb-6 rounded-2xl bg-white border border-[#E7E0D2] p-5">
      <div className="flex flex-wrap items-center gap-4">
        <div className="w-11 h-11 rounded-xl bg-[#FFF7ED] border border-[#FED7AA] grid place-items-center shrink-0">
          <Zap className="w-5 h-5 text-[#D97706]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-xs uppercase tracking-[0.18em] text-[#4B5563] font-semibold">Learn-mode audio cache</div>
          <div className="text-sm text-[#1F2937] mt-0.5">
            <span data-testid="tts-cache-total">{stats.total} total clips</span>
            {" · "}
            <span data-testid="tts-cache-cached" className="text-[#10B981] font-medium">{stats.cached} cached</span>
            {" · "}
            <span data-testid="tts-cache-uncached" className={stats.uncached ? "text-[#D97706] font-medium" : "text-[#4B5563]"}>
              {stats.uncached} pending
            </span>
          </div>
          <div className="text-xs text-[#4B5563] mt-1">
            Pre-generates audio for every item, passage and intro so the first student tap is instant and offline downloads complete faster.
          </div>
        </div>
        <button
          data-testid="warm-tts-btn"
          onClick={warmAll}
          disabled={busy || allCached}
          title={allCached ? "Nothing left to warm — every clip is cached." : `Warm ${stats.uncached} uncached clip(s). Skipped clips return instantly.`}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium border border-[#FED7AA] bg-[#FFF7ED] text-[#B45309] hover:border-[#D97706] hover:bg-[#FED7AA]/40 transition disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap">
          {busy
            ? <><Loader2 className="w-4 h-4 animate-spin" /> {progress ? `${progress.done}/${progress.total}` : "Warming…"}</>
            : allCached
              ? <>All cached ✓</>
              : <><Zap className="w-4 h-4" /> Warm cache ({stats.uncached})</>}
        </button>
      </div>
      {progress && (
        <div className="mt-3 h-1.5 bg-[#E7E0D2] rounded-full overflow-hidden" data-testid="warm-tts-progress">
          <div className="h-full bg-[#D97706] transition-all" style={{ width: `${pct}%` }} />
        </div>
      )}
    </div>
  );
}

/* -------------------- Module editor -------------------- */
function emptyModule() {
  return {
    code: "", n: 0, name: "", name_ar: "", intro: "",
    content_type: "reading", target_seconds: 60, expert_seconds: null, enabled: true,
    color: "#064E3B", items: [], passages: [], groups: [], section_meta: {},
    pass_pct: 100, reference_text: "", passage_name: "",
  };
}

function ModuleEditor({ target, onClose }) {
  const { user } = useAuth();
  const isTranslator = user?.role === "translator";
  const assignedLocales = user?.assigned_locales || [];
  const isNew = !!target.isNew;
  const [m, setM] = useState(emptyModule());
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("meta");   // meta · content · groups · image
  const [translatorDirty, setTranslatorDirty] = useState(false);
  const [translatorSaving, setTranslatorSaving] = useState(false);
  const translatorSaveRef = useRef(null);

  useEffect(() => {
    if (isNew) return;
    (async () => {
      try { const d = await adminGetModule(target.code); setM({ ...emptyModule(), ...d }); }
      catch { toast.error("Couldn't load module"); }
      finally { setLoading(false); }
    })();
  }, [target.code, isNew]);

  const update = (k, v) => setM((s) => ({ ...s, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      if (isNew) await adminCreateModule(m);
      else await adminUpdateModule(target.code, m);
      toast.success(isNew ? "Module created" : "Saved");
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const del = async () => {
    if (!window.confirm(`Delete module "${m.code}"? This cannot be undone.`)) return;
    try { await adminDeleteModule(target.code); toast.success("Deleted"); onClose(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  if (loading) return <div className="text-[#4B5563]">Loading…</div>;

  return (
    <div data-testid="admin-module-editor">
      <button onClick={onClose} data-testid="editor-back-btn"
        className="inline-flex items-center gap-1.5 text-sm text-[#4B5563] hover:text-[#1F2937] mb-4">
        <ChevronLeft className="w-4 h-4" /> Back to all modules
      </button>

      <div className="flex items-start justify-between flex-wrap gap-3 mb-5">
        <div>
          <div className="text-xs uppercase tracking-[0.22em] text-[#D97706]">{isNew ? "New module" : `Module ${m.n}`}</div>
          <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-[#1F2937] mt-1">{m.name || "(unnamed)"}</h1>
          <div className="text-xs text-[#4B5563] font-mono mt-0.5">{m.code || "—"}</div>
        </div>
        <div className="flex items-center gap-2">
          {isTranslator ? (
            <button data-testid="translator-save-btn" onClick={() => translatorSaveRef.current && translatorSaveRef.current()}
              disabled={!translatorDirty || translatorSaving}
              className="inline-flex items-center gap-1.5 text-sm px-4 py-2 rounded-full bg-[#064E3B] hover:bg-[#047857] text-white font-medium disabled:opacity-50">
              <Save className="w-4 h-4" /> {translatorSaving ? "Saving…" : "Save Changes"}
            </button>
          ) : (
            <>
              {!isNew && (
                <button data-testid="delete-module-btn" onClick={del}
                  className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-full border border-[#FECACA] text-[#7F1D1D] hover:bg-[#FEF2F2]">
                  <Trash2 className="w-3.5 h-3.5" /> Delete
                </button>
              )}
              <button data-testid="save-module-btn" onClick={save} disabled={saving}
                className="inline-flex items-center gap-1.5 text-sm px-4 py-2 rounded-full bg-[#064E3B] hover:bg-[#047857] text-white font-medium disabled:opacity-50">
                <Save className="w-4 h-4" /> {saving ? "Saving…" : (isNew ? "Create" : "Save")}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Tab strip — a Translator only ever gets Metadata; Content/Groups/
          Hero image stay Admin/Support-only, enforced on the backend by
          require_admin_or_support on every route those tabs call. */}
      <div className="flex flex-wrap gap-2 border-b border-[#E7E0D2] mb-5">
        {(isTranslator ? [{ id: "meta", label: "Metadata" }] : [
          { id: "meta",    label: "Metadata" },
          { id: "content", label: m.content_type === "mcq" ? "MCQ items" : m.content_type === "recording" ? "Passages" : "Reading items" },
          ...(m.content_type === "reading" ? [{ id: "groups", label: "Groups" }] : []),
          { id: "image",   label: "Hero image" },
        ]).map((t) => (
          <button key={t.id} data-testid={`editor-tab-${t.id}`} onClick={() => setTab(t.id)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition ${
              tab === t.id ? "border-[#064E3B] text-[#064E3B]" : "border-transparent text-[#4B5563] hover:text-[#1F2937]"
            }`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === "meta" && (isTranslator
        ? <TranslatorMetaTab m={m} code={target.code || m.code} assignedLocales={assignedLocales}
            onDirtyChange={setTranslatorDirty} onSavingChange={setTranslatorSaving}
            registerSave={(fn) => { translatorSaveRef.current = fn; }} />
        : <MetaTab m={m} update={update} isNew={isNew} code={target.code || m.code} />)}
      {!isTranslator && tab === "content" && <ContentTab m={m} update={update} />}
      {!isTranslator && tab === "groups" && !isNew && <GroupsEditor m={m} code={target.code || m.code} onChange={(groups) => update("groups", groups)} />}
      {!isTranslator && tab === "groups" && isNew && <div className="text-sm text-[#4B5563]">Save the module first, then return here to build groups.</div>}
      {!isTranslator && tab === "image" && <ImageTab m={m} code={target.code || m.code} isNew={isNew} onChange={(url) => update("hero_image_url", url)} />}
    </div>
  );
}

/* -------------------- Meta tab -------------------- */
const INSTRUCTION_LOCALES = [
  { code: "en", label: "English" },
  { code: "ta", label: "Tamil" },
  { code: "ur", label: "Urdu" },
  { code: "ms", label: "Bahasa Melayu" },
  { code: "sw", label: "Swahili" },
];

function MetaTab({ m, update, isNew, code }) {
  const [locale, setLocale] = useState("en");
  const [localeList, setLocaleList] = useState([{ code: "en", label: "English", dir: "ltr" }]);
  const [translations, setTranslations] = useState({});
  const [transName, setTransName] = useState("");
  const [transIntro, setTransIntro] = useState("");

  // Load published locales from the API
  useEffect(() => {
    adminListLocales()
      .then(data => {
        const published = (data.locales || []).filter(l => l.status === "published" && l.code !== "en");
        setLocaleList([{ code: "en", label: "English", dir: "ltr" }, ...published]);
      })
      .catch(() => {});
  }, []);

  // Fetch translations from i18n_base when switching to a non-English locale
  useEffect(() => {
    if (locale === "en" || isNew || !code) return;
    adminGetModuleTranslations(code, locale)
      .then(data => setTranslations(prev => ({ ...prev, [locale]: data })))
      .catch(() => setTranslations(prev => ({ ...prev, [locale]: null })));
  }, [locale, code, isNew]);

  // Sync local edit state whenever the fetched translation for the active locale changes
  useEffect(() => {
    if (locale === "en") { setTransName(""); setTransIntro(""); return; }
    setTransName(translations[locale]?.name ?? "");
    setTransIntro(translations[locale]?.intro ?? "");
  }, [locale, translations]);

  const saveTransField = async (field, value) => {
    if (!code || locale === "en") return;
    try {
      await adminSetModuleTranslations(code, locale, { [field]: value || null });
      setTranslations(prev => ({ ...prev, [locale]: { ...(prev[locale] || {}), [field]: value } }));
    } catch {
      toast.error("Failed to save translation");
    }
  };

  const SectionLabel = ({ children }) => (
    <div className="text-xs uppercase tracking-[0.22em] text-[#4B5563] mb-3">{children}</div>
  );

  const localePills = (
    <div className="flex flex-wrap gap-2">
      {localeList.map((l) => {
        const hasInstructions = Boolean(m.instructions?.[l.code]?.text || m.instructions?.[l.code]?.audio_url);
        const hasTimings = m.content_type === "reading" && Object.values(m.section_meta || {}).some((sec) => {
          const d = sec?.description;
          return typeof d === "object" && (d[l.code] || "").trim().length > 0;
        });
        const has = hasInstructions || hasTimings;
        return (
          <button key={l.code} data-testid={`meta-locale-${l.code}`} onClick={() => setLocale(l.code)}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition ${
              locale === l.code ? "bg-[#064E3B] text-white border-[#064E3B]" : "bg-white border-[#E7E0D2] text-[#1F2937] hover:border-[#064E3B]"
            }`}>
            {l.label} {has && <span className={locale === l.code ? "text-white" : "text-[#10B981]"}>●</span>}
          </button>
        );
      })}
    </div>
  );

  return (
    <div className="space-y-6">

      {/* ── Language switcher — at the top, controls Instructions & Section Timings below ── */}
      {localePills}

      {/* ── Identity ── */}
      <div className="pt-5 border-t border-[#E7E0D2]">
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Code" help="Used in URLs and progress tracking. Cannot be changed after creation.">
            <input data-testid="field-code" disabled={!isNew} value={m.code} onChange={(e) => update("code", e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))}
              className="w-full px-3 py-2 rounded-xl bg-white border border-[#E7E0D2] focus:border-[#064E3B] outline-none text-sm font-mono disabled:bg-[#F3F4F6] disabled:text-[#9CA3AF]" />
          </Field>
          <Field label="Order">
            <input data-testid="field-n" type="number" min={1} value={m.n} onChange={(e) => update("n", Number(e.target.value))}
              className="w-full px-3 py-2 rounded-xl bg-white border border-[#E7E0D2] focus:border-[#064E3B] outline-none text-sm" />
          </Field>
          {locale === "en" ? (
            <Field label="Name (English)">
              <input data-testid="field-name" value={m.name} onChange={(e) => update("name", e.target.value)}
                className="w-full px-3 py-2 rounded-xl bg-white border border-[#E7E0D2] focus:border-[#064E3B] outline-none text-sm" />
            </Field>
          ) : (
            <Field label={`Name (${localeList.find(l => l.code === locale)?.label || locale})`}>
              <input
                value={transName}
                onChange={(e) => setTransName(e.target.value)}
                onBlur={(e) => saveTransField("name", e.target.value)}
                placeholder="Not generated yet — edit or use Generate"
                className="w-full px-3 py-2 rounded-xl bg-white border border-[#E7E0D2] focus:border-[#064E3B] outline-none text-sm" />
            </Field>
          )}
          <Field label="Name (Arabic)">
            <input data-testid="field-name-ar" value={m.name_ar} onChange={(e) => update("name_ar", e.target.value)} dir="rtl"
              className="w-full px-3 py-2 rounded-xl bg-white border border-[#E7E0D2] focus:border-[#064E3B] outline-none text-base font-arabic" />
          </Field>
          <Field label="Maximum time (s)" help="The time limit to pass the timed assessment (full module, all items).">
            <input data-testid="field-target" type="number" min={0} value={m.target_seconds} onChange={(e) => update("target_seconds", Number(e.target.value))}
              className="w-full px-3 py-2 rounded-xl bg-white border border-[#E7E0D2] focus:border-[#064E3B] outline-none text-sm" />
          </Field>
          <Field label="Expert time (s)" help="Informational stretch goal shown to students — does not affect pass/fail.">
            <input data-testid="field-expert" type="number" min={0} value={m.expert_seconds ?? ""} onChange={(e) => update("expert_seconds", e.target.value === "" ? null : Number(e.target.value))}
              className="w-full px-3 py-2 rounded-xl bg-white border border-[#E7E0D2] focus:border-[#064E3B] outline-none text-sm" />
          </Field>
          <Field label="Content type">
            <select data-testid="field-type" value={m.content_type} onChange={(e) => update("content_type", e.target.value)}
              className="w-full px-3 py-2 rounded-xl bg-white border border-[#E7E0D2] focus:border-[#064E3B] outline-none text-sm">
              {CONTENT_TYPES.map((t) => <option key={t.code} value={t.code}>{t.label}</option>)}
            </select>
          </Field>
          <Field label="Status">
            <label className="flex items-center gap-2 text-sm mt-3">
              <input data-testid="field-enabled" type="checkbox" checked={m.enabled !== false} onChange={(e) => update("enabled", e.target.checked)}
                className="w-4 h-4 accent-[#064E3B]" />
              Module is active (students can see and use it)
            </label>
          </Field>
          {m.content_type === "mcq" && (
            <Field label="Pass percent" help="Module 8 SRS v2 mandates 100%. Lower this only if you fork the policy.">
              <input data-testid="field-passpct" type="number" min={0} max={100} value={m.pass_pct || 100} onChange={(e) => update("pass_pct", Number(e.target.value))}
                className="w-full px-3 py-2 rounded-xl bg-white border border-[#E7E0D2] focus:border-[#064E3B] outline-none text-sm" />
            </Field>
          )}
        </div>
      </div>

      {/* ── Intro text ── */}
      <div className="pt-5 border-t border-[#E7E0D2]">
        <SectionLabel>Intro text</SectionLabel>
        <p className="text-xs text-[#6B7280] mb-3">Shown as a paragraph with a TTS play button near the top of the module screen.</p>
        {locale === "en" ? (
          <>
            <textarea data-testid="field-intro" rows={3} value={m.intro} onChange={(e) => update("intro", e.target.value)}
              className="w-full px-3 py-2 rounded-xl bg-white border border-[#E7E0D2] focus:border-[#064E3B] outline-none text-sm" />
            <label className="mt-2 flex items-center gap-2 text-sm">
              <input data-testid="field-hide-intro" type="checkbox" checked={m.hide_intro === true} onChange={(e) => update("hide_intro", e.target.checked)}
                className="w-4 h-4 accent-[#064E3B]" />
              Hide intro text and its audio button on the module screen
            </label>
          </>
        ) : (
          <textarea
            rows={3}
            value={transIntro}
            onChange={(e) => setTransIntro(e.target.value)}
            onBlur={(e) => saveTransField("intro", e.target.value)}
            placeholder="Not generated yet — edit or use Generate"
            className="w-full px-3 py-2 rounded-xl bg-white border border-[#E7E0D2] focus:border-[#064E3B] outline-none text-sm" />
        )}
      </div>

      {/* ── Instructions ── */}
      <div className="pt-5 border-t border-[#E7E0D2]">
        <SectionLabel>Instructions</SectionLabel>
        {isNew ? (
          <div className="text-sm text-[#4B5563]">Save the module first, then return here to add voice instructions.</div>
        ) : (
          <InstructionsTab m={m} code={code} update={update} locale={locale} />
        )}
      </div>

      {/* ── Section Timings ── */}
      {m.content_type === "reading" && (
        <div className="pt-5 border-t border-[#E7E0D2]">
          <SectionLabel>Section Timings</SectionLabel>
          <SectionTimingsEditor m={m} update={update} locale={locale} />
        </div>
      )}

    </div>
  );
}

/* -------------------- Translator meta tab --------------------
   A deliberately separate component (not a branch inside MetaTab) so the
   Admin experience above is never at risk of being touched by this change.
   Only the locale-scoped fields the backend lets a Translator write are
   shown — name, intro, hide_intro (admin_get/set_module_translations) and
   instructions text + audio (admin_update_instructions_text,
   admin_upload/clear_instructions_audio) — each gated server-side by
   _require_module_locale_access to this translator's own assigned_locales.
   The language switcher is pre-filtered to just those locales. Every other
   Identity field, Content/Groups/Hero image, and Section Timings are
   Admin/Support-only and are not rendered here at all.

   Manual save: name/intro/instructions-text are held in local state and
   only sent to the server when "Save Changes" (rendered by the parent
   ModuleEditor, wired up via the registerSave/onDirtyChange/onSavingChange
   props) is clicked — matching Admin's own Metadata tab, which also defers
   everything to an explicit Save. Audio upload/record/delete inside
   InstructionsTab remain instant, exactly as they already are for Admin. */
function TranslatorMetaTab({ m, code, assignedLocales, onDirtyChange, onSavingChange, registerSave }) {
  const [locale, setLocale] = useState(assignedLocales[0] || null);
  const [localeMeta, setLocaleMeta] = useState({}); // code -> { label, native, dir }
  const [translations, setTranslations] = useState({}); // locale -> { name, intro, sections } (working, possibly-unsaved copy)
  const [localM, setLocalM] = useState(m); // working copy of the module doc — instructions/hide_intro edited here
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const fetchedLocalesRef = useRef(new Set());

  const localUpdate = (k, v) => { setLocalM((s) => ({ ...s, [k]: v })); setDirty(true); };

  useEffect(() => {
    adminListLocales()
      .then((data) => {
        const meta = {};
        (data.locales || []).forEach((l) => { meta[l.code] = l; });
        setLocaleMeta(meta);
      })
      .catch(() => {});
  }, []);

  // Fetch name/intro once per locale per visit to this tab — never
  // re-fetches a locale already loaded this session, so switching back to a
  // locale you were mid-edit on doesn't clobber the unsaved text with what's
  // still on the server.
  useEffect(() => {
    if (!locale || !code || fetchedLocalesRef.current.has(locale)) return;
    fetchedLocalesRef.current.add(locale);
    adminGetModuleTranslations(code, locale)
      .then((data) => setTranslations((prev) => ({ ...prev, [locale]: data })))
      .catch(() => setTranslations((prev) => ({ ...prev, [locale]: {} })));
  }, [locale, code]);

  const setField = (field, value) => {
    setTranslations((prev) => ({ ...prev, [locale]: { ...(prev[locale] || {}), [field]: value } }));
    setDirty(true);
  };

  // Section descriptions arrive as an array ({rule, label, description}),
  // not a flat field — update just the one row being edited, in place.
  const setSectionField = (rule, value) => {
    setTranslations((prev) => {
      const cur = prev[locale] || {};
      const sections = (cur.sections || []).map((s) => (s.rule === rule ? { ...s, description: value } : s));
      return { ...prev, [locale]: { ...cur, sections } };
    });
    setDirty(true);
  };

  const saveAll = async () => {
    if (saving || !code || !locale) return;
    setSaving(true);
    try {
      const sections = translations[locale]?.sections;
      await adminSetModuleTranslations(code, locale, {
        name: translations[locale]?.name || null,
        intro: translations[locale]?.intro || null,
        hide_intro: localM.hide_intro === true,
        sections: sections ? Object.fromEntries(sections.map((s) => [s.rule, s.description || ""])) : undefined,
      });
      await adminUpdateInstructionsText(code, locale, localM.instructions?.[locale]?.text || "");
      setDirty(false);
      toast.success("Saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => { onDirtyChange && onDirtyChange(dirty); }, [dirty, onDirtyChange]);
  useEffect(() => { onSavingChange && onSavingChange(saving); }, [saving, onSavingChange]);
  useEffect(() => { registerSave && registerSave(saveAll); });

  if (!assignedLocales.length) {
    return <div className="text-sm text-[#4B5563]">No language is assigned to your account yet — ask an admin to assign one.</div>;
  }

  const currentName = translations[locale]?.name ?? "";
  const currentSections = translations[locale]?.sections || [];
  const currentIntro = translations[locale]?.intro ?? "";

  return (
    <div className="space-y-6" data-testid="translator-meta-tab">
      <div className="flex flex-wrap gap-2">
        {assignedLocales.map((code_) => {
          const l = localeMeta[code_];
          return (
            <button key={code_} data-testid={`meta-locale-${code_}`} onClick={() => setLocale(code_)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition ${
                locale === code_ ? "bg-[#064E3B] text-white border-[#064E3B]" : "bg-white border-[#E7E0D2] text-[#1F2937] hover:border-[#064E3B]"
              }`}>
              {l?.label || l?.native || code_}
            </button>
          );
        })}
      </div>

      <div className="pt-5 border-t border-[#E7E0D2]">
        <Field label={`Name (${localeMeta[locale]?.label || locale})`}>
          <input
            data-testid="translator-field-name"
            value={currentName}
            onChange={(e) => setField("name", e.target.value)}
            placeholder="Not translated yet"
            className="w-full px-3 py-2 rounded-xl bg-white border border-[#E7E0D2] focus:border-[#064E3B] outline-none text-sm" />
        </Field>
      </div>

      <div className="pt-5 border-t border-[#E7E0D2]">
        <div className="text-xs uppercase tracking-[0.22em] text-[#4B5563] mb-3">Intro text</div>
        <p className="text-xs text-[#6B7280] mb-3">Shown as a paragraph with a TTS play button near the top of the module screen.</p>
        <textarea
          data-testid="translator-field-intro"
          rows={3}
          value={currentIntro}
          onChange={(e) => setField("intro", e.target.value)}
          placeholder="Not translated yet"
          className="w-full px-3 py-2 rounded-xl bg-white border border-[#E7E0D2] focus:border-[#064E3B] outline-none text-sm" />
        <label className="mt-2 flex items-center gap-2 text-sm">
          <input data-testid="translator-field-hide-intro" type="checkbox" checked={localM.hide_intro === true}
            onChange={(e) => localUpdate("hide_intro", e.target.checked)}
            className="w-4 h-4 accent-[#064E3B]" />
          Hide intro text and its audio button on the module screen
        </label>
      </div>

      <div className="pt-5 border-t border-[#E7E0D2]">
        <div className="text-xs uppercase tracking-[0.22em] text-[#4B5563] mb-3">Instructions</div>
        <InstructionsTab m={localM} code={code} update={localUpdate} locale={locale} />
      </div>

      {/* Section descriptions — the per-sub-section blurb shown inside a
          module's timed test screen (e.g. "Other cases of joining." on
          Joined Words' section 6f). Lives on the module's own
          section_meta, same field Admin's Section Timings editor writes;
          previously invisible to Translators entirely, which is why
          searching for this text on the main Translations page (a
          different content model — i18n_keys only) always came up empty. */}
      {currentSections.length > 0 && (
        <div className="pt-5 border-t border-[#E7E0D2]">
          <div className="text-xs uppercase tracking-[0.22em] text-[#4B5563] mb-3">Section descriptions</div>
          <p className="text-xs text-[#6B7280] mb-3">Shown to students inside each timed sub-section's test screen.</p>
          <div className="space-y-3">
            {currentSections.map((s) => (
              <Field key={s.rule} label={`${s.label} (${s.rule})`}>
                <textarea
                  data-testid={`translator-field-section-${s.rule}`}
                  rows={2}
                  value={s.description || ""}
                  onChange={(e) => setSectionField(s.rule, e.target.value)}
                  placeholder="Not translated yet"
                  className="w-full px-3 py-2 rounded-xl bg-white border border-[#E7E0D2] focus:border-[#064E3B] outline-none text-sm" />
              </Field>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* -------------------- Content tab -------------------- */
function ContentTab({ m, update }) {
  if (m.content_type === "recording") return <PassagesEditor m={m} update={update} />;
  if (m.content_type === "mcq") return <McqEditor m={m} update={update} />;
  return <ItemsEditor m={m} update={update} />;
}

function ItemsEditor({ m, update }) {
  const [csvMode, setCsvMode] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const items = useMemo(() => m.items || [], [m.items]);
  const setItems = (next) => update("items", next);

  const csv = useMemo(() => items.map((it) => [it.ar || "", it.translit || "", it.rule || ""].join(",")).join("\n"), [items]);
  const applyCsv = (text) => {
    const next = text.split("\n").map((line) => {
      const [ar, translit, rule] = line.split(",").map((s) => (s || "").trim());
      if (!ar) return null;
      return { ar, translit: translit || "", rule: rule || "" };
    }).filter(Boolean);
    setItems(next);
  };

  // Each handler mirrors the FULL raw field set the backend persists
  // (narrated_audio_id/mime, picture_id/mime — not just the display URL)
  // into local state. Upload endpoints now return these ids explicitly.
  // Without this, the top-level Save button PUTs this whole `items` array
  // back to the server and, since local state never carried the id fields,
  // would silently wipe whatever was just uploaded.
  const itemAudio = async (i, file) => {
    if (!file) return;
    try {
      const r = await adminUploadItemAudio(m.code, i, file);
      const n = [...items]; n[i] = { ...n[i], narrated_audio_id: r.narrated_audio_id, narrated_audio_mime: r.narrated_audio_mime, narrated_audio_url: r.audio_url }; setItems(n);
      toast.success(`Item ${i + 1} audio uploaded`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
  };
  const clearItemAudio = async (i) => {
    if (!window.confirm("Remove narrated audio for this item? It will fall back to AI TTS.")) return;
    try {
      await adminClearItemAudio(m.code, i);
      const n = [...items]; n[i] = { ...n[i] };
      delete n[i].narrated_audio_url; delete n[i].narrated_audio_id; delete n[i].narrated_audio_mime;
      setItems(n);
      toast.success("Cleared");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const itemPicture = async (i, file) => {
    if (!file) return;
    try {
      const r = await adminUploadItemPicture(m.code, i, file);
      const n = [...items]; n[i] = { ...n[i], picture_id: r.picture_id, picture_mime: r.picture_mime, picture_url: r.picture_url }; setItems(n);
      toast.success(`Item ${i + 1} picture uploaded`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
  };
  const clearItemPicture = async (i) => {
    if (!window.confirm("Remove this item's picture?")) return;
    try {
      await adminClearItemPicture(m.code, i);
      const n = [...items]; n[i] = { ...n[i] };
      delete n[i].picture_url; delete n[i].picture_id; delete n[i].picture_mime;
      setItems(n);
      toast.success("Cleared");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const bulkUpload = async (filesList, startIdx) => {
    const files = Array.from(filesList || []);
    if (!files.length) return;
    setBulkBusy(true); setBulkResult(null);
    try {
      const r = await adminBulkUploadItemAudio(m.code, files, startIdx);
      setBulkResult(r);
      const next = [...items];
      r.results.forEach((row) => {
        if (row.ok) next[row.idx] = { ...next[row.idx], narrated_audio_id: row.narrated_audio_id, narrated_audio_mime: row.narrated_audio_mime, narrated_audio_url: row.audio_url };
      });
      setItems(next);
      toast.success(`Uploaded ${r.uploaded} of ${files.length}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Bulk upload failed"); }
    finally { setBulkBusy(false); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="text-sm text-[#4B5563]">{items.length} items · {items.filter((it) => it.narrated_audio_url).length} narrated</div>
        <div className="flex items-center gap-2">
          <button onClick={() => setBulkOpen((v) => !v)} data-testid="bulk-audio-toggle"
            className="text-xs px-3 py-1.5 rounded-full border border-[#E7E0D2] hover:border-[#064E3B] text-[#1F2937] inline-flex items-center gap-1.5">
            <FileAudio className="w-3.5 h-3.5" /> Bulk audio
          </button>
          <button onClick={() => setCsvMode((v) => !v)} data-testid="toggle-csv-mode"
            className="text-xs px-3 py-1.5 rounded-full border border-[#E7E0D2] hover:border-[#064E3B] text-[#1F2937]">
            {csvMode ? "Switch to table" : "Edit as CSV"}
          </button>
          {!csvMode && (
            <button data-testid="add-item-btn" onClick={() => setItems([...items, { ar: "", translit: "", rule: "" }])}
              className="text-xs px-3 py-1.5 rounded-full bg-[#064E3B] text-white hover:bg-[#047857]">+ Add row</button>
          )}
        </div>
      </div>

      {bulkOpen && (
        <BulkAudioPanel busy={bulkBusy} result={bulkResult} itemCount={items.length} onUpload={bulkUpload} />
      )}

      {csvMode ? (
        <div>
          <div className="text-xs text-[#4B5563] mb-2">One item per line, fields separated by commas: <code className="bg-[#FFF7ED] px-1 py-0.5 rounded">arabic,transliteration,rule</code></div>
          <textarea data-testid="csv-textarea" defaultValue={csv} onBlur={(e) => applyCsv(e.target.value)} rows={Math.min(20, Math.max(8, items.length))}
            className="w-full px-3 py-2 rounded-xl bg-white border border-[#E7E0D2] focus:border-[#064E3B] outline-none text-sm font-mono leading-relaxed" />
        </div>
      ) : (
        <div className="rounded-xl border border-[#E7E0D2] overflow-hidden">
          <table className="w-full text-sm" data-testid="items-table">
            <thead className="bg-[#FDFBF7] text-[#4B5563] text-xs uppercase tracking-[0.16em]">
              <tr><th className="px-3 py-2 text-left">#</th><th className="px-3 py-2 text-right">Arabic</th><th className="px-3 py-2 text-left">Transliteration</th><th className="px-3 py-2 text-left">Rule</th><th className="px-3 py-2 text-center">Audio</th><th className="px-3 py-2 text-center">Picture</th><th className="px-3 py-2"></th></tr>
            </thead>
            <tbody>
              {items.map((it, i) => (
                <tr key={i} className="border-t border-[#E7E0D2]">
                  <td className="px-3 py-2 text-xs text-[#4B5563] tabular-nums w-12">{i + 1}</td>
                  <td className="px-3 py-2"><input dir="rtl" value={it.ar || ""} onChange={(e) => { const n = [...items]; n[i] = { ...n[i], ar: e.target.value }; setItems(n); }}
                    className="w-full px-2 py-1.5 bg-transparent text-right font-arabic text-lg outline-none focus:bg-[#FFF7ED] rounded" /></td>
                  <td className="px-3 py-2"><input value={it.translit || ""} onChange={(e) => { const n = [...items]; n[i] = { ...n[i], translit: e.target.value }; setItems(n); }}
                    placeholder="qaala" className="w-full px-2 py-1.5 bg-transparent text-sm italic outline-none focus:bg-[#FFF7ED] rounded" /></td>
                  <td className="px-3 py-2"><input value={it.rule || ""} onChange={(e) => { const n = [...items]; n[i] = { ...n[i], rule: e.target.value }; setItems(n); }}
                    placeholder="(section tag)" className="w-full px-2 py-1.5 bg-transparent text-sm outline-none focus:bg-[#FFF7ED] rounded" /></td>
                  <td className="px-3 py-2 text-center" data-testid={`item-audio-cell-${i}`}>
                    {it.narrated_audio_url ? (
                      <div className="flex flex-col items-center gap-1">
                        <audio controls preload="none" src={`${process.env.REACT_APP_BACKEND_URL}${it.narrated_audio_url}`} className="h-6 w-28" />
                        <div className="flex items-center gap-1.5">
                          <label className="inline-flex items-center gap-0.5 text-[10px] text-[#4B5563] hover:text-[#064E3B] cursor-pointer" title="Replace">
                            <Upload className="w-3 h-3" />
                            <input type="file" accept="audio/*" hidden onChange={(e) => itemAudio(i, e.target.files?.[0])} />
                          </label>
                          <button onClick={() => clearItemAudio(i)} className="text-[#9CA3AF] hover:text-[#EF4444]" title="Remove narration"><X className="w-3 h-3" /></button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center gap-1">
                        <label className="inline-flex items-center gap-1 text-xs text-[#4B5563] hover:text-[#064E3B] cursor-pointer">
                          <Upload className="w-3 h-3" /> Upload
                          <input type="file" accept="audio/*" hidden onChange={(e) => itemAudio(i, e.target.files?.[0])} />
                        </label>
                        <AdminAudioRecorderButton filename={`${m.code}-item-${i}.webm`} onRecorded={(file) => itemAudio(i, file)} />
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-center" data-testid={`item-picture-cell-${i}`}>
                    {it.picture_url ? (
                      <div className="flex items-center justify-center gap-1.5">
                        <img src={`${process.env.REACT_APP_BACKEND_URL}${it.picture_url}`} alt="" className="w-8 h-8 rounded object-cover border border-[#E7E0D2]" />
                        <button onClick={() => clearItemPicture(i)} className="text-[#9CA3AF] hover:text-[#EF4444]" title="Remove picture"><X className="w-3 h-3" /></button>
                      </div>
                    ) : (
                      <label className="inline-flex items-center gap-1 text-xs text-[#4B5563] hover:text-[#064E3B] cursor-pointer">
                        <ImageIcon className="w-3 h-3" /> Add
                        <input type="file" accept="image/*" hidden onChange={(e) => itemPicture(i, e.target.files?.[0])} />
                      </label>
                    )}
                  </td>
                  <td className="px-3 py-2 w-12 text-right">
                    <button onClick={() => { const n = items.filter((_, j) => j !== i); setItems(n); }}
                      className="text-[#9CA3AF] hover:text-[#EF4444]" title="Delete row">
                      <X className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr><td colSpan={7} className="px-3 py-6 text-center text-sm text-[#4B5563]">No items yet — click "Add row" to start.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function BulkAudioPanel({ busy, result, itemCount, onUpload }) {
  const [startIdx, setStartIdx] = useState(0);
  const inputRef = useRef(null);
  return (
    <div className="mb-4 rounded-2xl border border-[#FED7AA] bg-[#FFF7ED] p-4" data-testid="bulk-audio-panel">
      <div className="flex items-start gap-2">
        <FileAudio className="w-5 h-5 text-[#B45309] flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <div className="font-medium text-[#1F2937] text-sm">Bulk-upload narrated audio</div>
          <div className="text-xs text-[#4B5563] mt-1 leading-relaxed">
            Select multiple audio files (mp3 / m4a / wav · ≤ 5 MB each). Files are assigned to items in the order you pick them, starting at the item number below. So put your filenames in the correct sequence (most file pickers respect filename order — name them <code className="bg-white px-1 rounded">001.mp3 · 002.mp3 …</code>).
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <label className="text-xs text-[#4B5563] flex items-center gap-2">
              Start at item #
              <input data-testid="bulk-start-idx" type="number" min={1} max={itemCount} value={startIdx + 1}
                onChange={(e) => setStartIdx(Math.max(0, Math.min(itemCount - 1, Number(e.target.value) - 1)))}
                className="w-20 px-2 py-1 rounded-lg bg-white border border-[#E7E0D2] text-sm tabular-nums" />
            </label>
            <button data-testid="bulk-audio-pick" onClick={() => inputRef.current?.click()} disabled={busy}
              className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-[#064E3B] text-white hover:bg-[#047857] disabled:opacity-50">
              <Upload className="w-3.5 h-3.5" /> {busy ? "Uploading…" : "Choose files"}
            </button>
            <input ref={inputRef} type="file" accept="audio/*" multiple hidden
              onChange={(e) => onUpload(e.target.files, startIdx)} />
          </div>
          {result && (
            <div className="mt-3 text-xs text-[#4B5563]">
              Uploaded <b>{result.uploaded}</b> / {result.results.length}.
              {result.results.some((r) => !r.ok) && (
                <ul className="mt-1 list-disc ml-4 text-[#7F1D1D]">
                  {result.results.filter((r) => !r.ok).map((r, i) => (
                    <li key={i}>{r.filename}: {r.reason}</li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function McqEditor({ m, update }) {
  const items = m.items || [];
  const setItems = (next) => update("items", next);

  // Mirrors the full raw field set (narrated_audio_id/mime, not just the
  // display URL) into local state — matches ItemsEditor's pattern, since the
  // top-level Save button PUTs this whole `items` array back to the server.
  const itemAudio = async (i, file) => {
    if (!file) return;
    if (items[i]?.narrated_audio_url && !window.confirm("Replace the existing audio for this word?")) return;
    try {
      const r = await adminUploadItemAudio(m.code, i, file);
      const n = [...items]; n[i] = { ...n[i], narrated_audio_id: r.narrated_audio_id, narrated_audio_mime: r.narrated_audio_mime, narrated_audio_url: r.audio_url }; setItems(n);
      toast.success(`Question ${i + 1} audio uploaded`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
  };
  const clearItemAudio = async (i) => {
    if (!window.confirm("Remove this word's recorded audio? It will fall back to the Audio URL field (or TTS if that's empty too).")) return;
    try {
      await adminClearItemAudio(m.code, i);
      const n = [...items]; n[i] = { ...n[i] };
      delete n[i].narrated_audio_url; delete n[i].narrated_audio_id; delete n[i].narrated_audio_mime;
      setItems(n);
      toast.success("Cleared");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-sm text-[#4B5563]">{items.length} MCQ questions</div>
        <button data-testid="add-mcq-btn" onClick={() => setItems([...items, { prompt: "", answer_ar: "", options: [{ ar: "", translit: "" }, { ar: "", translit: "" }, { ar: "", translit: "" }, { ar: "", translit: "" }], audio_url: "" }])}
          className="text-xs px-3 py-1.5 rounded-full bg-[#064E3B] text-white hover:bg-[#047857]">+ Add question</button>
      </div>
      {items.map((it, i) => (
        <div key={i} className="rounded-xl border border-[#E7E0D2] bg-white p-4">
          <div className="flex items-start justify-between gap-3">
            <div className="text-xs text-[#4B5563] font-mono">Q{i + 1}</div>
            <button onClick={() => setItems(items.filter((_, j) => j !== i))}
              className="text-[#9CA3AF] hover:text-[#EF4444]"><X className="w-4 h-4" /></button>
          </div>
          <Field label="Prompt (English)">
            <input value={it.prompt || ""} onChange={(e) => { const n = [...items]; n[i] = { ...n[i], prompt: e.target.value }; setItems(n); }}
              placeholder="Which word do you hear?" className="w-full px-3 py-2 rounded-xl bg-white border border-[#E7E0D2] focus:border-[#064E3B] outline-none text-sm" />
          </Field>
          <Field label="Audio URL" help="Optional fallback. Public mp3/wav URL — used only if no recorded/uploaded audio below is set.">
            <input value={it.audio_url || ""} onChange={(e) => { const n = [...items]; n[i] = { ...n[i], audio_url: e.target.value }; setItems(n); }}
              placeholder="https://..." className="w-full px-3 py-2 rounded-xl bg-white border border-[#E7E0D2] focus:border-[#064E3B] outline-none text-sm" />
          </Field>
          <Field label="Recorded/uploaded audio" help="Takes priority over the Audio URL fallback above and over TTS.">
            <div className="flex items-center gap-3 flex-wrap" data-testid={`mcq-audio-cell-${i}`}>
              {it.narrated_audio_url ? (
                <>
                  <audio controls preload="none" src={`${process.env.REACT_APP_BACKEND_URL}${it.narrated_audio_url}`} className="h-8" />
                  <label className="inline-flex items-center gap-1 text-xs text-[#4B5563] hover:text-[#064E3B] cursor-pointer" title="Replace">
                    <Upload className="w-3.5 h-3.5" /> Replace
                    <input type="file" accept="audio/*" hidden onChange={(e) => itemAudio(i, e.target.files?.[0])} />
                  </label>
                  <button onClick={() => clearItemAudio(i)} className="text-[#9CA3AF] hover:text-[#EF4444]" title="Remove"><X className="w-3.5 h-3.5" /></button>
                </>
              ) : (
                <>
                  <label className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-[#E7E0D2] hover:border-[#064E3B] text-[#1F2937] cursor-pointer">
                    <Upload className="w-3.5 h-3.5" /> Upload
                    <input type="file" accept="audio/*" hidden onChange={(e) => itemAudio(i, e.target.files?.[0])} />
                  </label>
                  <AdminAudioRecorderButton filename={`${m.code}-mcq-${i}.webm`} onRecorded={(file) => itemAudio(i, file)} />
                </>
              )}
            </div>
          </Field>
          <div className="mt-3">
            <div className="text-xs uppercase tracking-[0.22em] text-[#4B5563] mb-2">Options · click the radio to mark the correct answer</div>
            <div className="grid sm:grid-cols-2 gap-2">
              {(it.options || []).map((o, k) => (
                <div key={k} className="flex items-center gap-2">
                  <input type="radio" name={`q${i}-correct`} checked={it.answer_ar === o.ar && !!o.ar}
                    onChange={() => { const n = [...items]; n[i] = { ...n[i], answer_ar: o.ar }; setItems(n); }} />
                  <input dir="rtl" value={o.ar || ""} placeholder="Arabic"
                    onChange={(e) => { const n = [...items]; const nopts = [...n[i].options]; nopts[k] = { ...nopts[k], ar: e.target.value }; n[i] = { ...n[i], options: nopts }; setItems(n); }}
                    className="flex-1 px-3 py-1.5 rounded-lg bg-white border border-[#E7E0D2] focus:border-[#064E3B] outline-none text-right font-arabic" />
                  <input value={o.translit || ""} placeholder="translit"
                    onChange={(e) => { const n = [...items]; const nopts = [...n[i].options]; nopts[k] = { ...nopts[k], translit: e.target.value }; n[i] = { ...n[i], options: nopts }; setItems(n); }}
                    className="w-28 px-2 py-1.5 rounded-lg bg-white border border-[#E7E0D2] focus:border-[#064E3B] outline-none text-xs italic" />
                </div>
              ))}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function PassagesEditor({ m, update }) {
  const passages = m.passages || [];
  const setPassages = (next) => update("passages", next);

  const uploadAudio = async (i, file) => {
    if (!file) return;
    try {
      const r = await adminUploadPassageAudio(m.code, i, file);
      const n = [...passages]; n[i] = { ...n[i], narrated_audio_id: r.narrated_audio_id, narrated_audio_mime: r.narrated_audio_mime, narrated_audio_url: r.audio_url }; setPassages(n);
      toast.success(`Passage ${i + 1} audio uploaded`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-sm text-[#4B5563]">{passages.length} passages · {passages.filter((p) => p.narrated_audio_url || p.audio_url).length} with audio</div>
        <button data-testid="add-passage-btn" onClick={() => setPassages([...passages, { code: "", name: "", prelude: "", ar: "" }])}
          className="text-xs px-3 py-1.5 rounded-full bg-[#064E3B] text-white hover:bg-[#047857]">+ Add passage</button>
      </div>
      {passages.map((p, i) => (
        <div key={i} className="rounded-xl border border-[#E7E0D2] bg-white p-4 space-y-3" data-testid={`passage-editor-${i}`}>
          <div className="flex items-start justify-between gap-3">
            <div className="text-xs text-[#4B5563] font-mono">Passage #{i + 1}</div>
            <button onClick={() => setPassages(passages.filter((_, j) => j !== i))}
              className="text-[#9CA3AF] hover:text-[#EF4444]"><X className="w-4 h-4" /></button>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Code (e.g. 9a)">
              <input value={p.code || ""} onChange={(e) => { const n = [...passages]; n[i] = { ...n[i], code: e.target.value }; setPassages(n); }}
                className="w-full px-3 py-2 rounded-xl bg-white border border-[#E7E0D2] outline-none text-sm font-mono" />
            </Field>
            <Field label="Display name">
              <input value={p.name || ""} onChange={(e) => { const n = [...passages]; n[i] = { ...n[i], name: e.target.value }; setPassages(n); }}
                className="w-full px-3 py-2 rounded-xl bg-white border border-[#E7E0D2] outline-none text-sm" />
            </Field>
          </div>
          <Field label="Prelude (e.g. ta'awwudh + basmalah)">
            <input dir="rtl" value={p.prelude || ""} onChange={(e) => { const n = [...passages]; n[i] = { ...n[i], prelude: e.target.value }; setPassages(n); }}
              className="w-full px-3 py-2 rounded-xl bg-white border border-[#E7E0D2] outline-none text-base font-arabic text-right" />
          </Field>
          <Field label="Passage text (Arabic)">
            <textarea dir="rtl" rows={4} value={p.ar || ""} onChange={(e) => { const n = [...passages]; n[i] = { ...n[i], ar: e.target.value }; setPassages(n); }}
              className="w-full px-3 py-2 rounded-xl bg-white border border-[#E7E0D2] outline-none text-lg font-arabic text-right leading-loose" />
          </Field>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Maximum time (seconds)" help="Self-assessment passes if the recording falls within this window.">
              <input type="number" min={0} value={p.target_seconds ?? ""} onChange={(e) => { const n = [...passages]; n[i] = { ...n[i], target_seconds: e.target.value === "" ? null : Number(e.target.value) }; setPassages(n); }}
                className="w-full px-3 py-2 rounded-xl bg-white border border-[#E7E0D2] outline-none text-sm" />
            </Field>
            <Field label="Expert time (seconds)" help="Informational stretch goal — does not affect pass/fail.">
              <input type="number" min={0} value={p.expert_seconds ?? ""} onChange={(e) => { const n = [...passages]; n[i] = { ...n[i], expert_seconds: e.target.value === "" ? null : Number(e.target.value) }; setPassages(n); }}
                className="w-full px-3 py-2 rounded-xl bg-white border border-[#E7E0D2] outline-none text-sm" />
            </Field>
          </div>
          {/* Audio block — accept URL, upload, or live record */}
          <div className="rounded-xl bg-[#FFF7ED] border border-[#FED7AA] p-3">
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#B45309] mb-2 flex items-center gap-1.5">
              <Volume2 className="w-3 h-3" /> Recitation audio (used by the Learn button on this passage)
            </div>
            {p.narrated_audio_url ? (
              <div className="flex items-center gap-3 mb-2 flex-wrap">
                <audio controls preload="none" src={`${process.env.REACT_APP_BACKEND_URL}${p.narrated_audio_url}`} className="h-9" />
              </div>
            ) : null}
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="External audio URL (mp3/m4a)" help="Optional. If both URL and upload are set, the uploaded recording wins.">
                <input value={p.audio_url || ""} placeholder="https://..."
                  onChange={(e) => { const n = [...passages]; n[i] = { ...n[i], audio_url: e.target.value }; setPassages(n); }}
                  className="w-full px-3 py-2 rounded-xl bg-white border border-[#E7E0D2] outline-none text-sm font-mono" />
              </Field>
              <Field label="Upload or record narrated recitation (≤10 MB)">
                <div className="flex items-center gap-2 flex-wrap">
                  <label className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-white border border-[#E7E0D2] hover:border-[#064E3B] text-sm text-[#1F2937] cursor-pointer">
                    <Upload className="w-4 h-4" /> Choose file
                    <input data-testid={`passage-audio-${i}`} type="file" accept="audio/*" hidden onChange={(e) => uploadAudio(i, e.target.files?.[0])} />
                  </label>
                  <AdminAudioRecorderButton filename={`${m.code}-passage-${i}.webm`} onRecorded={(file) => uploadAudio(i, file)} />
                </div>
              </Field>
            </div>
          </div>

          {(p.ayahs || []).length > 0 && <AyahsEditor code={m.code} pidx={i} passage={p} passages={passages} setPassages={setPassages} />}
        </div>
      ))}
    </div>
  );
}

/**
 * Per-ayah editor — collapsed by default. Each ayah row shows the
 * verse text with tap-to-toggle red-word marking, a record/upload
 * audio override, and a "verified" checkbox for the auto-split verse
 * boundaries (see backend/data/fluency_v3.py — these were split by the
 * assistant from the existing verbatim text and need a qualified
 * reviewer's confirmation before being fully trusted).
 */
function AyahsEditor({ code, pidx, passage, passages, setPassages }) {
  const [open, setOpen] = useState(false);
  const ayahs = passage.ayahs || [];
  const unverifiedCount = ayahs.filter((a) => !a.verified).length;

  const setAyah = (aidx, patch) => {
    const nextAyahs = [...ayahs];
    nextAyahs[aidx] = { ...nextAyahs[aidx], ...patch };
    const nextPassages = [...passages];
    nextPassages[pidx] = { ...nextPassages[pidx], ayahs: nextAyahs };
    setPassages(nextPassages);
  };

  const toggleRedWord = async (aidx, word) => {
    const a = ayahs[aidx];
    const has = (a.red_words || []).includes(word);
    const next = has ? a.red_words.filter((w) => w !== word) : [...(a.red_words || []), word];
    setAyah(aidx, { red_words: next });
    try { await adminUpdateAyah(code, pidx, aidx, { red_words: next }); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not save red word"); }
  };

  const toggleVerified = async (aidx) => {
    const a = ayahs[aidx];
    const next = !a.verified;
    setAyah(aidx, { verified: next });
    try { await adminUpdateAyah(code, pidx, aidx, { verified: next }); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
  };

  // Free text — the verse itself. Editable directly now (previously only
  // derived fields like red_words could be touched), which is what lets an
  // admin author a brand-new page's ayahs one row at a time instead of
  // relying on the hardcoded word-count split that only covers the 5
  // originally-seeded passages.
  const setAyahText = async (aidx, value) => {
    setAyah(aidx, { ar: value });
    try { await adminUpdateAyah(code, pidx, aidx, { ar: value }); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
  };

  // Group tag — any ayahs sharing the same tag (e.g. "1-10") become one
  // combined test unit; leaving it blank keeps the ayah testing on its own.
  // Mirrors reading-module items' free-text Rule column exactly.
  const setAyahRule = async (aidx, value) => {
    const next = value || null;
    setAyah(aidx, { rule: next });
    try { await adminUpdateAyah(code, pidx, aidx, { rule: value }); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
  };

  const addAyah = async () => {
    try {
      const r = await adminAddAyah(code, pidx);
      setPassages(passages.map((p, i) => (i === pidx ? { ...p, ayahs: [...(p.ayahs || []), r.ayah] } : p)));
      setOpen(true);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add ayah"); }
  };

  const deleteAyah = async (aidx) => {
    if (!window.confirm(`Delete Ayah ${ayahs[aidx]?.n}? This can't be undone.`)) return;
    try {
      await adminDeleteAyah(code, pidx, aidx);
      const nextAyahs = ayahs.filter((_, i) => i !== aidx);
      const nextPassages = [...passages];
      nextPassages[pidx] = { ...nextPassages[pidx], ayahs: nextAyahs };
      setPassages(nextPassages);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not delete ayah"); }
  };

  // Seeded with a sensible starting value (proportional split of the
  // passage's own time by word count) — this is where admins take over
  // ownership of each ayah's pass/fail window, same as passage-level
  // Maximum/Expert time already work.
  const setAyahTiming = async (aidx, field, value) => {
    const n = value === "" ? null : Number(value);
    setAyah(aidx, { [field]: n });
    try { await adminUpdateAyah(code, pidx, aidx, { [field]: n }); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
  };

  const uploadAyahAudio = async (aidx, file) => {
    if (!file) return;
    try {
      const r = await adminUploadAyahAudio(code, pidx, aidx, file);
      setAyah(aidx, { narrated_audio_id: r.narrated_audio_id, narrated_audio_mime: r.narrated_audio_mime, narrated_audio_url: r.audio_url });
      toast.success(`Ayah ${ayahs[aidx].n} audio uploaded`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
  };

  const clearAyahAudio = async (aidx) => {
    try {
      await adminClearAyahAudio(code, pidx, aidx);
      setAyah(aidx, { narrated_audio_id: null, narrated_audio_mime: null, narrated_audio_url: null });
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not clear audio"); }
  };

  // New Words Audio — stored under its own new_words_audio_id/mime fields,
  // entirely separate from (and never overwriting) the Ayah Audio above.
  const uploadAyahNewWordsAudio = async (aidx, file) => {
    if (!file) return;
    try {
      const r = await adminUploadAyahNewWordsAudio(code, pidx, aidx, file);
      setAyah(aidx, { new_words_audio_id: r.new_words_audio_id, new_words_audio_mime: r.new_words_audio_mime, new_words_audio_url: r.audio_url });
      toast.success(`Ayah ${ayahs[aidx].n} New Words audio uploaded`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
  };

  const clearAyahNewWordsAudio = async (aidx) => {
    try {
      await adminClearAyahNewWordsAudio(code, pidx, aidx);
      setAyah(aidx, { new_words_audio_id: null, new_words_audio_mime: null, new_words_audio_url: null });
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not clear audio"); }
  };

  return (
    <div className="rounded-xl border border-[#E7E0D2] bg-[#FDFBF7] p-3">
      <div className="w-full flex items-center justify-between">
        <button type="button" onClick={() => setOpen((v) => !v)} className="flex-1 flex items-center justify-between text-left">
          <div className="text-xs font-medium text-[#1F2937] flex items-center gap-2">
            Ayahs ({ayahs.length})
            {unverifiedCount > 0 && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#FEF2F2] text-[#EF4444] text-[10px] font-semibold border border-[#FECACA]">
                <AlertTriangle className="w-3 h-3" /> {unverifiedCount} need verification
              </span>
            )}
          </div>
          {open ? <ChevronUp className="w-4 h-4 text-[#4B5563]" /> : <ChevronDown className="w-4 h-4 text-[#4B5563]" />}
        </button>
        <button type="button" data-testid={`add-ayah-btn-${pidx}`} onClick={addAyah}
          className="ml-3 text-xs px-2.5 py-1 rounded-full bg-[#064E3B] text-white hover:bg-[#047857]">+ Add ayah</button>
      </div>

      {open && (
        <div className="mt-3 space-y-3">
          {ayahs.map((a, aidx) => (
            <div key={aidx} className="rounded-lg bg-white border border-[#E7E0D2] p-3" data-testid={`ayah-editor-${pidx}-${aidx}`}>
              <div className="flex items-center justify-between gap-2 mb-2">
                <span className="text-[10px] font-mono text-[#4B5563]">Ayah {a.n}</span>
                <div className="flex items-center gap-3">
                  <label className="inline-flex items-center gap-1.5 text-[10px] text-[#4B5563] cursor-pointer">
                    <input type="checkbox" checked={Boolean(a.verified)} onChange={() => toggleVerified(aidx)} />
                    {a.verified ? <span className="inline-flex items-center gap-1 text-[#10B981]"><Check className="w-3 h-3" /> Verified</span> : "Mark as verified"}
                  </label>
                  <button type="button" onClick={() => deleteAyah(aidx)} className="text-[#9CA3AF] hover:text-[#EF4444]" title="Delete ayah">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
              <textarea dir="rtl" rows={2} value={a.ar || ""} placeholder="Ayah text (Arabic)"
                data-testid={`ayah-text-${pidx}-${aidx}`}
                onChange={(e) => setAyahText(aidx, e.target.value)}
                className="w-full px-3 py-2 rounded-lg bg-[#FDFBF7] border border-[#E7E0D2] outline-none text-lg font-arabic text-right leading-loose mb-2" />
              <div className="flex flex-wrap gap-1.5 justify-end" dir="rtl">
                {(a.ar || "").split(/\s+/).filter(Boolean).map((w, wi) => {
                  const isRed = (a.red_words || []).includes(w);
                  return (
                    <button key={wi} type="button" onClick={() => toggleRedWord(aidx, w)}
                      title="Click to mark/unmark as an already-learnt (red) word"
                      className={`font-quran text-lg px-1 rounded transition ${isRed ? "bg-[#FEF2F2] text-[#EF4444]" : "hover:bg-[#FFF7ED] text-[#1F2937]"}`}>
                      {w}
                    </button>
                  );
                })}
              </div>
              <Field label="Group (ayahs sharing the same tag test together, e.g. '1-10')" className="mt-2">
                <input value={a.rule || ""} placeholder="Leave blank to test this ayah on its own"
                  data-testid={`ayah-group-${pidx}-${aidx}`}
                  onChange={(e) => setAyahRule(aidx, e.target.value)}
                  className="w-full px-2 py-1.5 rounded-lg bg-white border border-[#E7E0D2] outline-none text-xs" />
              </Field>
              <div className="mt-2 grid grid-cols-2 gap-2">
                <label className="flex items-center gap-1.5 text-[10px] text-[#4B5563]">
                  Max time (s)
                  <input type="number" min={0} value={a.target_seconds ?? ""} data-testid={`ayah-target-${pidx}-${aidx}`}
                    onChange={(e) => setAyahTiming(aidx, "target_seconds", e.target.value)}
                    className="w-16 px-1.5 py-1 rounded-lg bg-white border border-[#E7E0D2] outline-none text-xs" />
                </label>
                <label className="flex items-center gap-1.5 text-[10px] text-[#4B5563]">
                  Expert time (s)
                  <input type="number" min={0} value={a.expert_seconds ?? ""} data-testid={`ayah-expert-${pidx}-${aidx}`}
                    onChange={(e) => setAyahTiming(aidx, "expert_seconds", e.target.value)}
                    className="w-16 px-1.5 py-1 rounded-lg bg-white border border-[#E7E0D2] outline-none text-xs" />
                </label>
              </div>
              <div className="mt-2 flex items-center gap-2">
                {a.narrated_audio_url ? (
                  <>
                    <a href={`${process.env.REACT_APP_BACKEND_URL}${a.narrated_audio_url}`} target="_blank" rel="noreferrer"
                      className="inline-flex items-center gap-1 text-[10px] text-[#064E3B] hover:underline"><Volume2 className="w-3 h-3" /> Preview</a>
                    <button onClick={() => clearAyahAudio(aidx)} className="text-[10px] text-[#EF4444] hover:underline">Clear audio</button>
                  </>
                ) : (
                  <label className="inline-flex items-center gap-1.5 px-2 py-1 rounded-lg bg-white border border-[#E7E0D2] hover:border-[#064E3B] text-[10px] text-[#1F2937] cursor-pointer">
                    <Upload className="w-3 h-3" /> Upload audio override
                    <input type="file" accept="audio/*" hidden onChange={(e) => uploadAyahAudio(aidx, e.target.files?.[0])} />
                  </label>
                )}
              </div>
              <div className="mt-2 flex items-center gap-2">
                <span className="text-[10px] uppercase tracking-wider text-[#4B5563]">New Words Audio</span>
                {a.new_words_audio_url ? (
                  <>
                    <a href={`${process.env.REACT_APP_BACKEND_URL}${a.new_words_audio_url}`} target="_blank" rel="noreferrer"
                      data-testid={`ayah-new-words-audio-preview-${pidx}-${aidx}`}
                      className="inline-flex items-center gap-1 text-[10px] text-[#064E3B] hover:underline"><Volume2 className="w-3 h-3" /> Preview</a>
                    <button onClick={() => clearAyahNewWordsAudio(aidx)} data-testid={`ayah-new-words-audio-clear-${pidx}-${aidx}`}
                      className="text-[10px] text-[#EF4444] hover:underline">Clear audio</button>
                  </>
                ) : (
                  <label className="inline-flex items-center gap-1.5 px-2 py-1 rounded-lg bg-white border border-[#E7E0D2] hover:border-[#064E3B] text-[10px] text-[#1F2937] cursor-pointer">
                    <Upload className="w-3 h-3" /> Upload New Words audio
                    <input type="file" accept="audio/*" hidden data-testid={`ayah-new-words-audio-input-${pidx}-${aidx}`}
                      onChange={(e) => uploadAyahNewWordsAudio(aidx, e.target.files?.[0])} />
                  </label>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* -------------------- Instructions tab -------------------- */
/**
 * "How to use this module" — a short voice + text explainer, authored
 * per language. Shown to students via a help button inside Learn mode.
 * `locale` is controlled by the shared language switcher in MetaTab.
 */
function InstructionsTab({ m, code, update, locale }) {
  const instructions = m.instructions || {};
  const entry = instructions[locale] || { text: "", audio_url: null };
  const [uploading, setUploading] = useState(false);

  const setLocaleEntry = (patch) => {
    const next = { ...instructions, [locale]: { ...entry, ...patch } };
    update("instructions", next);
  };

  const uploadAudio = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const r = await adminUploadInstructionsAudio(code, locale, file);
      setLocaleEntry({ audio_url: r.audio_url });
      toast.success("Instructions audio uploaded");
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); }
  };

  const clearAudio = async () => {
    try {
      await adminClearInstructionsAudio(code, locale);
      setLocaleEntry({ audio_url: null });
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not clear audio"); }
  };

  return (
    <div>
      {/* <Field label={`Instruction text (${INSTRUCTION_LOCALES.find((l) => l.code === locale)?.label})`}> */}
        <textarea data-testid="instructions-text" rows={4} value={entry.text || ""}
          onChange={(e) => setLocaleEntry({ text: e.target.value })}
          className="w-full px-3 py-2 rounded-xl bg-white border border-[#E7E0D2] focus:border-[#064E3B] outline-none text-sm" />
      {/* </Field> */}

      <div className="mt-5 rounded-xl bg-[#FFF7ED] border border-[#FED7AA] p-3">
        <div className="text-[10px] uppercase tracking-[0.22em] text-[#B45309] mb-2 flex items-center gap-1.5">
          <Volume2 className="w-3 h-3" /> Voice instructions ({locale}) — record or upload
        </div>
        {entry.audio_url ? (
          <div className="flex items-center gap-3 flex-wrap">
            <audio controls preload="none" src={`${process.env.REACT_APP_BACKEND_URL}${entry.audio_url}`} className="h-9" />
            <button onClick={clearAudio} className="text-xs text-[#EF4444] hover:underline">Clear audio</button>
          </div>
        ) : (
          <div className="flex items-center gap-2 flex-wrap">
            <label className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-white border border-[#E7E0D2] hover:border-[#064E3B] text-sm text-[#1F2937] cursor-pointer">
              <Upload className="w-4 h-4" /> {uploading ? "Uploading…" : "Choose file"}
              <input data-testid="instructions-audio-upload" type="file" accept="audio/*" hidden disabled={uploading} onChange={(e) => uploadAudio(e.target.files?.[0])} />
            </label>
            <AdminAudioRecorderButton filename={`${code}-instructions-${locale}.webm`} onRecorded={uploadAudio} disabled={uploading} />
          </div>
        )}
      </div>
    </div>
  );
}

/* -------------------- Image tab -------------------- */
function ImageTab({ m, code, isNew, onChange }) {
  const [busy, setBusy] = useState(false);
  if (isNew) return <div className="text-sm text-[#4B5563]">Save the module first, then return here to upload a hero image.</div>;

  const upload = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return toast.error("File too large (max 5 MB)");
    setBusy(true);
    try { const r = await adminUploadModuleImage(code, file); onChange?.(r.hero_image_url); toast.success("Image uploaded"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); }
  };

  return (
    <div>
      <div className="rounded-2xl border border-dashed border-[#E7E0D2] bg-[#FDFBF7] p-8 text-center">
        <ImageIcon className="w-10 h-10 mx-auto text-[#4B5563]" />
        <div className="mt-3 text-sm text-[#4B5563]">Drop or pick an image (jpg/png · max 5 MB). This shows on the module hero card.</div>
        <label className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#064E3B] hover:bg-[#047857] text-white text-sm font-medium cursor-pointer">
          <Upload className="w-4 h-4" /> {busy ? "Uploading…" : "Choose image"}
          <input data-testid="image-upload-input" type="file" accept="image/*" hidden onChange={(e) => upload(e.target.files?.[0])} />
        </label>
      </div>
      {m.hero_image_url && (
        <div className="mt-5 rounded-2xl overflow-hidden border border-[#E7E0D2] bg-white">
          <img src={m.hero_image_url} alt="" className="w-full max-h-72 object-cover" />
        </div>
      )}
    </div>
  );
}

/* -------------------- Reusable field -------------------- */
function Field({ label, help, className = "", children }) {
  return (
    <div className={className}>
      <label className="block text-xs uppercase tracking-[0.22em] text-[#4B5563] mb-1.5">{label}</label>
      {children}
      {help && <div className="mt-1 text-xs text-[#9CA3AF]">{help}</div>}
    </div>
  );
}
